import { motion } from "framer-motion";
import {
  Calendar,
  Clock,
  Edit2,
  Heart,
  ListMusic,
  MoreHorizontal,
  Play,
  Radio,
  Share2,
  Shuffle,
  Trash2,
} from "lucide-react";
import React, { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { TrackList } from "../components/TrackList";
import { radioEngine } from "../lib/RadioEngine";
import { useLocalLibraryStore } from "../stores/localLibraryStore";
import { useLocalRadioStore } from "../stores/LocalRadioStore";
import { usePlayerStore } from "../stores/playerStore";
import { formatTime } from "../utils/formatTime";

export const RadioStationDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { getRadioStation, updateRadioStation, deleteRadioStation } = useLocalRadioStore();
  const { setCurrentTrack, setIsPlaying, setQueue } = usePlayerStore();
  const { isContentLiked, toggleLike } = useLocalLibraryStore();
  const [showMenu, setShowMenu] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const station = getRadioStation(id || "");

  if (!station) {
    return (
      <div className="p-8 text-center">
        <h1 className="text-2xl font-bold mb-4">Radio station not found</h1>
        <button onClick={() => navigate(-1)} className="text-yellow-500 hover:underline">
          Go back
        </button>
      </div>
    );
  }

  const totalDuration = station.tracks.reduce((acc, track) => acc + track.duration, 0);

  const handlePlay = () => {
    if (station.tracks.length > 0) {
      const nextTrack = radioEngine.getNextTrack(station.id);
      if (nextTrack) {
        setCurrentTrack(nextTrack);
        setQueue(station.tracks);
        setIsPlaying(true);
      }
    }
  };

  const handleShuffle = () => {
    if (station.tracks.length > 0) {
      const shuffledTracks = [...station.tracks].sort(() => Math.random() - 0.5);
      setCurrentTrack(shuffledTracks[0]);
      setQueue(shuffledTracks);
      setIsPlaying(true);
    }
  };

  const handleShare = async () => {
    try {
      await navigator.share({
        title: station.name,
        text: `Check out this radio station: ${station.name}`,
        url: window.location.href,
      });
    } catch (error) {
      console.error("Error sharing:", error);
    }
  };

  const handleDelete = () => {
    deleteRadioStation(station.id);
    navigate("/radio");
  };

  const handleRemoveTrack = (trackId: string) => {
    const updatedTracks = station.tracks.filter((track) => track.id !== trackId);
    updateRadioStation(station.id, { tracks: updatedTracks, trackCount: updatedTracks.length });
  };

  const isLiked = isContentLiked(station.id, "radio");

  return (
    <div className="min-h-screen bg-gradient-to-b from-zinc-800/50 to-zinc-900">
      <div className="max-w-7xl mx-auto px-3 md:px-6 py-4 md:py-8">
        {/* Radio Station Header */}
        <div className="flex flex-col md:flex-row justify-between mb-8">
          <div className="md:text-left">
            <h1 className="text-3xl md:text-4xl font-bold mb-4">{station.name}</h1>
            {station.description && <p className="text-zinc-400 mb-4">{station.description}</p>}
            <div className="flex flex-wrap justify-start gap-4 gap-y-2 text-zinc-400">
              <div className="flex items-center gap-2">
                <Radio className="w-5 h-5" />
                <span>Radio Station</span>
              </div>
              <div className="flex items-center gap-2">
                <ListMusic className="w-5 h-5" />
                <span>{station.trackCount} tracks</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="w-5 h-5" />
                <span>{formatTime(totalDuration)}</span>
              </div>
              <div className="flex items-center gap-2">
                <Calendar className="w-5 h-5" />
                <span>{new Date(station.createdAt).toLocaleDateString()}</span>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-4 mt-4 md:mt-0">
            <button
              onClick={handlePlay}
              className="w-full min-w-32 md:w-auto px-4 py-2 bg-yellow-500 text-white rounded-full hover:bg-yellow-600 transition flex items-center justify-center gap-2"
            >
              <Play className="w-5 h-5" />
              <span className="hidden md:inline">Play</span>
            </button>
            <button
              onClick={handleShuffle}
              className="p-2 md:p-3 text-zinc-400 hover:text-white transition"
              title="Shuffle"
            >
              <Shuffle className="w-5 h-5" />
            </button>
            <button
              onClick={() => toggleLike(station.id, "radio", "local")}
              className={`p-2 md:p-3 transition ${isLiked ? "text-yellow-500" : "text-zinc-400 hover:text-white"}`}
            >
              <Heart className="w-5 h-5" fill={isLiked ? "currentColor" : "none"} />
            </button>
            <button onClick={handleShare} className="p-2 md:p-3 text-zinc-400 hover:text-white transition">
              <Share2 className="w-5 h-5" />
            </button>
            <div className="relative">
              <button
                onClick={() => setShowMenu(!showMenu)}
                className="p-2 md:p-3 text-zinc-400 hover:text-white transition"
              >
                <MoreHorizontal className="w-5 h-5" />
              </button>

              {showMenu && (
                <div className="absolute right-0 mt-2 w-40 md:w-48 bg-zinc-800 rounded-md shadow-lg z-50">
                  <button
                    onClick={() => {
                      setShowMenu(false);
                      // TODO: Implement edit functionality
                    }}
                    className="w-full px-4 py-2 text-left flex items-center gap-2 hover:bg-zinc-700"
                  >
                    <Edit2 className="w-4 h-4" />
                    Edit Station
                  </button>
                  <button
                    onClick={() => {
                      setShowMenu(false);
                      setShowDeleteConfirm(true);
                    }}
                    className="w-full px-4 py-2 text-left flex items-center gap-2 hover:bg-zinc-700 text-red-400"
                  >
                    <Trash2 className="w-4 h-4" />
                    Delete Station
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Track List */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-zinc-800/50 rounded-lg overflow-hidden"
        >
          <TrackList
            tracks={station.tracks}
            onRemove={handleRemoveTrack}
            showHeader={true}
            title={station.name}
            subtitle={`${station.trackCount} tracks in rotation`}
          />
        </motion.div>

        {/* Dialogs */}
        <ConfirmDialog
          isOpen={showDeleteConfirm}
          onClose={() => setShowDeleteConfirm(false)}
          onConfirm={handleDelete}
          title="Delete Radio Station"
          message={`Are you sure you want to delete "${station.name}"? This action cannot be undone.`}
          confirmLabel="Delete"
          type="danger"
        />
      </div>
    </div>
  );
};
